/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { DropzoneOptions } from 'react-dropzone';
import NumberFormat from '../../utils/number-format';
import { FileDropZone } from '../files/FileDropZone';

function showFileFn(i: number, saving: boolean, file: File, removeFn: (i: number) => void) {
  const { size, name } = file;
  const fileSize = <NumberFormat value={size / 1024} />;
  return (
    <div className='file' key={i}>
      <i style={{ cursor: saving ? 'not-allowed' : '' }}
        onClick={() => !saving && removeFn(i)}
        className='fa fa-times' />
      {name} ({fileSize}kb)
    </div>
  )
}

function showFileList(files: File[], saving: boolean, removeFn: () => void) {

  if (!Array.isArray(files) || files.length === 0) {
    return <></>;
  }

  return (
    <div className='fileList'>
      {files.map((file, i) => showFileFn(i, saving, file, removeFn))}
    </div>
  );
}

interface FileZoneProps extends Pick<DropzoneOptions, 'accept' | 'multiple'> {
  saving?: boolean;
  disabled?: boolean;
  title?: string;
  handleFilesAdded: (files: File[]) => void;
  dropText: (files: File[], saving: boolean, rmFn: (i: number) => void) => JSX.Element;
  removeFn?: (files: File[]) => void;
  renderFileList: (files: File[], saving: boolean, rmFn: (i: number) => void) => JSX.Element;
}

export default function FileZone(props: FileZoneProps) {

  const { saving = false, disabled = false, title, handleFilesAdded, accept, dropText, removeFn, renderFileList, multiple = false } = props;

  const [files, setFiles] = useState<File[]>([]);

  const handleFn = (files: File[]) => {
    setFiles(files);
    if (typeof handleFilesAdded === 'function') {
      handleFilesAdded(files);
    }
  };

  const rmFn = (i: number) => {
    const newFiles = [...files];
    newFiles.splice(i, 1);
    handleFn(newFiles);
    if (typeof removeFn === 'function') {
      removeFn(files);
    }
  };

  const bodyFn = typeof dropText === 'function' ? dropText : (_a?: any, _b?: any, _c?: any) => dropText;

  return (
    <div>
      {title ? <label>{title}</label> : null}
      <div className='dropzone-container'>
        <FileDropZone accept={accept} disabled={saving || disabled} onDrop={handleFn} multiple={multiple}>
          {bodyFn(files, saving, rmFn)}
        </FileDropZone>
        {renderFileList(files, saving, rmFn)}
      </div>
    </div>
  );
}

FileZone.defaultProps = {
  renderFileList: showFileList,
  accept: {
    '*': []
  },
  dropText: 'Click here to add files',
};
